/**
 * NL-006 / NL-010 / NL-014 / NL-018 (2026-06-09) — one-shot welcome
 * banner shown on the client's first visit to a freshly unlocked
 * section. Per-step dismissal persists in localStorage keyed by
 * (clientId, stepNumber, variantKey) so the banner never re-renders
 * across page reloads or device switches with the same browser.
 *
 * The banner self-gates: it renders nothing if either (a) the
 * client's `current_step` is below `stepNumber`, or (b) the
 * (clientId, stepNumber, variantKey) tuple has been dismissed.
 *
 * Copy lives under `client.welcome.step{N}.{variantKey}.{title,body}`.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useClientStep } from '@/lib/use-client-step';
import { useAuth } from '@/app/auth-context';

interface StepEntryBannerProps {
  readonly stepNumber: number;
  readonly variantKey: string;
}

function bannerKey(clientId: string, step: number, variant: string): string {
  return `h3eromap.banner.${clientId}.step${step}.${variant}`;
}

export function isBannerDismissed(
  clientId: string,
  step: number,
  variant: string,
): boolean {
  if (typeof window === 'undefined') return true;
  return window.localStorage.getItem(bannerKey(clientId, step, variant)) === '1';
}

export function dismissBanner(
  clientId: string,
  step: number,
  variant: string,
): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(bannerKey(clientId, step, variant), '1');
}

export function StepEntryBanner({
  stepNumber,
  variantKey,
}: StepEntryBannerProps): JSX.Element | null {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { step, isClient } = useClientStep();
  const clientId = user?.clientId ?? null;

  const [visible, setVisible] = useState<boolean>(() => {
    if (!clientId) return false;
    return !isBannerDismissed(clientId, stepNumber, variantKey);
  });

  if (!isClient) return null;
  if (!clientId) return null;
  if ((step ?? 0) < stepNumber) return null;
  if (!visible) return null;

  return (
    <section
      role="status"
      aria-live="polite"
      className="mb-s5 rounded-lg border border-flame-deep/40 bg-flame-glow/20 p-s5 flex items-start gap-s4"
      data-testid={`step-entry-banner-step${stepNumber}-${variantKey}`}
    >
      <div className="flex-1">
        <h3 className="font-display text-display-sm text-ink">
          {t(`client.welcome.step${stepNumber}.${variantKey}.title`)}
        </h3>
        <p className="mt-s2 text-ink/80">
          {t(`client.welcome.step${stepNumber}.${variantKey}.body`)}
        </p>
      </div>
      <button
        type="button"
        onClick={() => {
          dismissBanner(clientId, stepNumber, variantKey);
          setVisible(false);
        }}
        className="shrink-0 text-gray-1 hover:text-flame-deep text-2xl leading-none"
        aria-label="Dismiss"
        data-testid={`step-entry-banner-dismiss-step${stepNumber}-${variantKey}`}
      >
        ✕
      </button>
    </section>
  );
}
